import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { AdminUser, ResetPasswordPayload } from '../../types/auth'
import { FormField, Input } from '../../components/ui/FormField'
import { tokens } from '../../styles/tokens'

interface Props {
  user: AdminUser
  onChange: (payload: ResetPasswordPayload) => void
}

const UserInfo = styled.div`
  background: ${tokens.purple50};
  border: 1px solid ${tokens.purple100};
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${tokens.purple600}, ${tokens.purple400});
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: white;
  font-weight: 500;
  flex-shrink: 0;
`

const UserDetails = styled.div``

const UserName = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: ${tokens.purple900};
`

const UserEmail = styled.p`
  font-size: 11px;
  color: ${tokens.purple400};
`

const Warning = styled.p`
  font-size: 12px;
  color: #854f0b;
  background: #faeeda;
  border: 1px solid #fac775;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 16px;
  line-height: 1.5;
`

export function ResetPasswordForm({ user, onChange }: Props) {
  const [password, setPassword] = useState('')
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    onChangeRef.current({ password })
  }, [password])

  const initials = user.name
    ? user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : user.email[0].toUpperCase()

  return (
    <>
      <UserInfo>
        <UserAvatar>{initials}</UserAvatar>
        <UserDetails>
          <UserName>{user.name ?? user.email}</UserName>
          <UserEmail>{user.email}</UserEmail>
        </UserDetails>
      </UserInfo>

      <Warning>The user will be required to change their password on next login.</Warning>

      <FormField label="New temporary password" hint="Min. 8 characters">
        <Input
          type="password"
          placeholder="Temporary password"
          value={password}
          onChange={e => setPassword(e.target.value)}
        />
      </FormField>
    </>
  )
}
